import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw, Search, X } from 'lucide-react';
import axios from 'axios';

interface AdminDuplicateViewProps {
  selectedTable: string;
  tables?: Array<{ tableName: string; displayName: string }>;
}

interface DuplicateGroup {
  id_catalogo: string;
  count: number;
  products: any[];
}

const AdminDuplicateView: React.FC<AdminDuplicateViewProps> = ({ selectedTable, tables }) => {
  const { t } = useTranslation();
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);
  const [uniqueProducts, setUniqueProducts] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Get formatted table display name
  const getTableDisplayName = () => {
    if (tables) {
      const table = tables.find(t => t.tableName === selectedTable);
      return table?.displayName || selectedTable;
    }
    return selectedTable;
  };

  const loadDuplicates = async () => {
    if (!selectedTable) {
      setDuplicates([]);
      setTotalProducts(0);
      setUniqueProducts(0);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Cargar todos los productos de la toma actual
      const response = await axios.get(`/api/tables/${selectedTable}/data?limit=50000&offset=0`);
      const allProducts = response.data.data || [];
      
      // Agrupar por id_catalogo
      const catalogoGroups: { [key: string]: any[] } = {};
      let _productsWithCatalogo = 0;
      
      allProducts.forEach((product: any) => {
        const id_catalogo = product.id_catalogo || product.ID_CATALOGO;
        
        if (id_catalogo) {
          _productsWithCatalogo++;
          if (!catalogoGroups[id_catalogo]) {
            catalogoGroups[id_catalogo] = [];
          }
          catalogoGroups[id_catalogo].push(product);
        }
      });

      // Encontrar duplicados (grupos con más de 1 producto)
      const duplicateGroups: DuplicateGroup[] = [];
      
      Object.entries(catalogoGroups).forEach(([id_catalogo, products]) => {
        if (products.length > 1) {
          duplicateGroups.push({
            id_catalogo,
            count: products.length,
            products
          });
        }
      });

      // Ordenar por cantidad de duplicados (mayor primero)
      duplicateGroups.sort((a, b) => b.count - a.count);

      setDuplicates(duplicateGroups);
      setTotalProducts(allProducts.length);
      setUniqueProducts(Object.keys(catalogoGroups).length);

    } catch (err) {
      setError(t('admin.diagnostics.error_loading_duplicates'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDuplicates();
  }, [selectedTable]);

  const getProductDisplayInfo = (product: any) => {
    return {
      titulo: product.titulo || product.title || product.TITULO || t('admin.diagnostics.no_title'),
      marca: product.Marca || product.marca || product.MARCA || t('admin.diagnostics.no_brand'),
      precio: product.precio || product.original_price || product.PRECIO || 0,
      sitio: product.sitio || product.SITIO || t('admin.diagnostics.no_site'),
      url: product.url || product.URL || product.link || t('admin.diagnostics.no_url'),
      imagen: product.imagen || product.image || product.IMAGEN || product.Imagen || product.IMAGE || '',
      pagina: product.pagina || product.page || product.PAGE || product.page_number || product.PAGINA || '-',
      orden: product.orden || product.order || product.ORDER || product.orden_en_pag || product.page_order || product.position || product.POSITION || product.ORDEN || product.pos || '-',
      orderInPage: product.orderInPage || product.order_in_page || product.ORDER_IN_PAGE || product.ordenEnPagina || '-'
    };
  };

  if (!selectedTable) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center py-8">
          <Search className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('admin.diagnostics.duplicate_analysis')}</h2>
          <p className="text-gray-600">{t('admin.diagnostics.select_table_for_duplicates')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header con estadísticas */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            <h2 className="text-lg font-semibold text-gray-800">
              {t('admin.diagnostics.duplicates_title', { table: getTableDisplayName() })}
            </h2>
          </div>
          <button
            onClick={loadDuplicates}
            disabled={loading}
            className="flex items-center space-x-2 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span>{loading ? t('admin.diagnostics.executing') : t('admin.diagnostics.refresh')}</span>
          </button>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-600">{totalProducts}</div>
            <div className="text-sm text-blue-800">{t('admin.diagnostics.total_products')}</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">{uniqueProducts}</div>
            <div className="text-sm text-green-800">{t('admin.diagnostics.unique_ids')}</div>
          </div>
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-orange-600">{duplicates.length}</div>
            <div className="text-sm text-orange-800">{t('admin.diagnostics.duplicate_groups')}</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-600">
              {duplicates.reduce((sum, group) => sum + group.count, 0)}
            </div>
            <div className="text-sm text-red-800">{t('admin.diagnostics.total_duplicates')}</div>
          </div>
        </div>

        {/* Mensaje de estado */}
        {!loading && duplicates.length === 0 && (
          <div className="mt-4 p-4 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-green-800 font-medium">
                ✅ {t('admin.diagnostics.no_duplicates')}
              </span>
            </div>
          </div>
        )}

        {!loading && duplicates.length > 0 && (
          <div className="mt-4 p-4 bg-red-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="text-red-800 font-medium">
                ⚠️ {t('admin.diagnostics.duplicates_warning', { count: duplicates.length })}
              </span>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <X className="h-4 w-4 text-red-500" />
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}
      </div>

      {/* Lista de duplicados */}
      {!loading && duplicates.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {t('admin.diagnostics.duplicate_details', { count: duplicates.length })}
          </h3>
          
          <div className="space-y-6">
            {duplicates.map((group, index) => (
              <div key={group.id_catalogo} className="border-l-4 border-red-400 pl-4">
                <div className="flex items-center space-x-2 mb-3">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  <h4 className="font-semibold text-gray-800">
                    {t('admin.diagnostics.catalog_id')}: <span className="text-red-600">{group.id_catalogo}</span>
                  </h4>
                  <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
                    {group.count} {t('admin.diagnostics.duplicates_count')}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {group.products.map((product, productIndex) => {
                    const info = getProductDisplayInfo(product);
                    return (
                      <div key={productIndex} className="bg-gray-50 rounded-lg p-3 border">
                        <div className="flex space-x-3">
                          {/* Imagen del producto */}
                          <div className="flex-shrink-0">
                            {info.imagen ? (
                              <img 
                                src={info.imagen} 
                                alt={info.titulo}
                                className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0zMiAyOEMyOS43OTA5IDI4IDI4IDI5Ljc5MDkgMjggMzJDMjggMzQuMjA5MSAyOS43OTA5IDM2IDMyIDM2QzM0LjIwOTEgMzYgMzYgMzQuMjA5MSAzNiAzMkMzNiAyOS43OTA5IDM0LjIwOTEgMjggMzIgMjhaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0yNCA0MEw0MCA0MEw0MCAyNEwyNCAyNEwyNCA0MFoiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+';
                                }}
                              />
                            ) : (
                              <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-xs border border-gray-200">
                                {t('admin.diagnostics.no_image')}
                              </div>
                            )}
                          </div>
                          
                          {/* Información del producto */}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm space-y-1">
                              <div className="font-medium text-gray-800 truncate" title={info.titulo}>
                                {info.titulo}
                              </div>
                              <div className="text-gray-600 text-xs">
                                <span className="font-medium">{info.marca}</span> • ${info.precio}
                              </div>
                              
                              {/* Información en líneas separadas para mejor legibilidad */}
                              <div className="text-gray-500 text-xs space-y-0.5">
                                <div className="truncate">
                                  <span className="font-medium">{t('admin.diagnostics.site')}:</span> {info.sitio}
                                </div>
                                
                                <div className="flex items-center space-x-3">
                                  <span>
                                    <span className="font-medium">{t('admin.diagnostics.page')}:</span> {info.pagina}
                                  </span>
                                  <span>
                                    <span className="font-medium">{t('admin.diagnostics.order')}:</span> {info.orden}
                                  </span>
                                  <span>
                                    <span className="font-medium">{t('admin.diagnostics.order_in_page')}:</span> {info.orderInPage}
                                  </span>
                                </div>
                                
                                <div className="truncate">
                                  <span className="font-medium">{t('admin.diagnostics.url')}:</span> 
                                  {info.url !== t('admin.diagnostics.no_url') ? (
                                    <a 
                                      href={info.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 ml-1 break-all"
                                    >
                                      {info.url.length > 25 ? `${info.url.substring(0, 25)}...` : info.url}
                                    </a>
                                  ) : (
                                    <span className="ml-1 text-gray-400">{info.url}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="animate-spin h-6 w-6 text-blue-500 mr-2" />
            <span className="text-gray-600">{t('admin.diagnostics.analyzing_duplicates', { table: getTableDisplayName() })}...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDuplicateView; 